import { useState, useEffect, useMemo, useRef } from 'react';
import { 
    X, Search, ChevronDown, ChevronRight, ChevronLeft, 
    PlayCircle, BookOpen, MessageCircle, Target, Sparkles,
    BarChart3, Trophy, Share2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getSignedGuideUrl } from '@/services/api';

const GUIDE_DATA = [
    {
        section: "Learn",
        icon: BookOpen,
        color: "text-orange-500",
        items: [
            { id: 'start-chapter', title: 'Start a Chapter', description: 'Select subject & chapter, choose Guided or Free mode, explore the chapter view with textbook, annotations & Budlee', videoUrl: 'settings/detailed_guide/Learn/video1.mp4' },
            { id: 'learning-packs', title: 'Learning Packs', description: 'Watch a short video → Read the concept → Answer a Curiosity Question → Solve MCQs', videoUrl: 'settings/detailed_guide/Learn/video2.mp4' },
            { id: 'class-notes', title: 'Class Notes', description: "Upload your teacher's classroom notes and ask Budlee anything about them, and share with your classmates.", videoUrl: 'settings/detailed_guide/Learn/video3.mp4' },
            { id: 'short-notes', title: 'Short Notes', description: 'Quick AI-generated summaries of every section, ready for revision', videoUrl: 'settings/detailed_guide/Learn/video4.mp4' },
            { id: 'bookmarks-revision', title: 'Bookmarks & Revision', description: 'Bookmark any video, text, or content — find it all in one place when you revise', videoUrl: 'settings/detailed_guide/Learn/video5.mp4' },
            { id: 'notes-highlights', title: 'Notes & Highlights', description: 'Highlight text, add personal notes with Budlee beside you', videoUrl: 'settings/detailed_guide/Learn/video6.mp4' },
            { id: 'points-streaks-badges', title: 'Points, Streaks & Badges', description: 'Earn points as you learn, keep your daily streak alive, and unlock badges along the way', videoUrl: null },
        ]
    },
    {
        section: "Chat",
        icon: MessageCircle,
        color: "text-green-400",
        items: [
            { id: 'chat-anywhere', title: 'Chat from Anywhere', description: "Whether you're in Textbook, Class Notes, or Short Notes — tap Budlee and ask", videoUrl: 'settings/detailed_guide/Chat/video1.mp4' },
            { id: 'ask-budlee', title: 'Got a Doubt? Ask Budlee', description: 'Type your question and get answers instantly with videos, images & diagrams — switch between simple and detailed explanations', videoUrl: 'settings/detailed_guide/Chat/video2.mp4' },
            { id: 'chat-curiosity', title: 'Chat to Curiosity', description: 'Curious about something? Jump straight from any chat answer into the Curiosity Centre to explore more', videoUrl: 'settings/detailed_guide/Chat/video3.mp4' },
        ]
    },
    {
        section: "Quest",
        icon: Target,
        color: "text-orange-400",
        items: [
            { id: 'practice-mode', title: 'Practice Mode', description: 'Choose your question type (MCQ, true/false, short answer, written, numerical), set the difficulty, and start practicing', videoUrl: 'settings/detailed_guide/Quest/video1.mp4' },
            { id: 'practice-ai', title: 'Practice with AI', description: 'Write your answer → Get an AI score → Read feedback → Rewrite & improve → See the answer', videoUrl: 'settings/detailed_guide/Quest/video2.mp4' },
            { id: 'test-mode', title: 'Test Mode', description: 'Take full chapter tests, review your results with AI, and save to draft to continue later', videoUrl: 'settings/detailed_guide/Quest/video3.mp4' },
        ]
    },
    {
        section: "Curiosity",
        icon: Sparkles,
        color: "text-purple-400",
        items: [
            { id: 'chapter-curiosity', title: 'Chapter Curiosity', description: 'Every chapter comes with supporting videos, simulations, courses & books to explore', videoUrl: 'settings/detailed_guide/Curiosity/video1.mp4' },
            { id: 'budlee-ai-curiosity', title: 'Budlee AI in Curiosity', description: 'Ask Budlee about anything while watching videos or exploring', videoUrl: 'settings/detailed_guide/Curiosity/video2.mp4' },
            { id: 'explore-more-topics', title: 'Explore More Topics', description: 'Discover advanced domains like AI, Space, Engineering, Business, Communication, Creativity & more', videoUrl: 'settings/detailed_guide/Curiosity/video3.mp4' },
            { id: 'topic-resources', title: 'Topic Resources', description: 'Access videos, articles, courses, and connect with real experts', videoUrl: 'settings/detailed_guide/Curiosity/video4.mp4' },
        ]
    },
    {
        section: "Analytics",
        icon: BarChart3,
        color: "text-blue-400",
        items: [
            { id: 'student-analytics', title: 'Student Analytics', description: 'Discover what you love learning, how you spend your time, and what excites you most', videoUrl: 'settings/detailed_guide/Analytics/video1.mp4' },
            { id: 'share-parents-friends', title: 'Share with Parents & Friends', description: 'Send your progress and interest insights directly to your parents. and share your achievements with friends', videoUrl: null },
        ]
    },
    {
        section: "Achievements",
        icon: Trophy,
        color: "text-yellow-500",
        items: [
            { id: 'leaderboard', title: 'Leaderboard', description: 'See the top performers— all-time, weekly, and monthly rankings', videoUrl: 'settings/detailed_guide/Achievements/video1.mp4' },
            { id: 'shop', title: 'Shop', description: 'Spend your earned points on Budlee characters, avatars, profile frames, boosters, icons & badges', videoUrl: null },
        ]
    },
    {
        section: "Share",
        icon: Share2,
        color: "text-pink-400",
        items: [
            { id: 'share-what-you-learn', title: 'Share what you learn', description: 'chats, videos, and notes in one tap', videoUrl: 'settings/detailed_guide/Share/video1.mp4' },
        ]
    }
];

import { useMediaQuery } from '@/hooks/useMediaQuery';

export default function DetailedUserGuide({ isOpen, onClose }) {
    const isMobile = useMediaQuery('(max-width: 767px)');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeItemId, setActiveItemId] = useState(GUIDE_DATA[0].items[0].id);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const scrollContainerRef = useRef(null);
    const [videoSrc, setVideoSrc] = useState(null);
    const [prefetchCache, setPrefetchCache] = useState({});
    const [isVideoReady, setIsVideoReady] = useState(false);

    // Close menu when switching to desktop
    useEffect(() => {
        if (!isMobile) setIsMobileMenuOpen(false);
    }, [isMobile]);

    // Flatten items for easier navigation and search
    const allItems = useMemo(() => GUIDE_DATA.flatMap(s => s.items.map(i => ({ ...i, section: s.section }))), []);
    
    const filteredData = useMemo(() => {
        if (!searchQuery) return GUIDE_DATA;
        return GUIDE_DATA.map(section => ({
            ...section,
            items: section.items.filter(item => 
                item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
                section.section.toLowerCase().includes(searchQuery.toLowerCase())
            )
        })).filter(section => section.items.length > 0);
    }, [searchQuery]);

    const activeItem = useMemo(() => allItems.find(i => i.id === activeItemId) || allItems[0], [activeItemId, allItems]);
    const activeIndex = useMemo(() => allItems.findIndex(i => i.id === activeItemId), [activeItemId, allItems]);

    const handleNext = () => {
        if (activeIndex < allItems.length - 1) {
            setActiveItemId(allItems[activeIndex + 1].id);
        }
    };

    const handleBack = () => {
        if (activeIndex > 0) {
            setActiveItemId(allItems[activeIndex - 1].id);
        }
    };

    useEffect(() => {
        if (isOpen) {
            // Reset to first item when opened if none active
            if (!activeItemId) setActiveItemId(allItems[0].id);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    // Video signing and Warming logic
    useEffect(() => {
        if (!isOpen) return;

        const loadVideo = async () => {
            if (!activeItem?.videoUrl) {
                setVideoSrc(null);
                setIsVideoReady(false);
                return;
            }

            // Check cache first
            if (prefetchCache[activeItem.id]) {
                setVideoSrc(prefetchCache[activeItem.id]);
            } else {
                const url = await getSignedGuideUrl(activeItem.videoUrl);
                setVideoSrc(url);
            }
            setIsVideoReady(false); // Reset ready state when source changes

            // Prefetch the next item
            const nextItem = allItems[activeIndex + 1];
            if (nextItem?.videoUrl && !prefetchCache[nextItem.id]) {
                getSignedGuideUrl(nextItem.videoUrl).then(url => {
                    if (url) setPrefetchCache(prev => ({ ...prev, [nextItem.id]: url }));
                });
            }
        };

        loadVideo();
    }, [activeItem, activeIndex, isOpen, allItems]);

    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose?.();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[11000] flex items-center justify-center animate-in fade-in duration-200">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            
            <div className={cn(
                "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-1.5rem)] rounded-2xl h-auto max-h-[calc(100dvh-(env(safe-area-inset-top)+env(safe-area-inset-bottom)+9rem))] flex flex-col bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] shadow-2xl p-0 overflow-y-auto overflow-x-hidden outline-none",
                "md:max-h-[85vh] md:max-w-6xl md:w-full md:border md:flex-row"
            )}>
                {/* Left Sidebar (Desktop) */}
                <div className="hidden md:flex flex-col w-80 border-r border-[hsl(var(--card-border))] bg-[hsl(var(--sidebar-bg))]">
                    <div className="p-6 border-b border-[hsl(var(--card-border))]">
                        <h2 className="text-base font-bold text-foreground">User Guide</h2>
                        <p className="text-sm text-muted-foreground mt-1">Explore all features</p>
                        
                        <div className="mt-6 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <input 
                                type="text"
                                placeholder="Search guides..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--teal-vivid))]/50 transition-all"
                            />
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-6 scrollbar-thin">
                        {filteredData.map((section) => (
                            <div key={section.section} className="space-y-2">
                                <h3 className="px-3 mb-1 text-[11.5px] font-black uppercase tracking-[0.1em] text-muted-foreground flex items-center gap-2">
                                    <section.icon className={cn("w-4 h-4", section.color)} />
                                    {section.section}
                                </h3>
                                <div className="space-y-1">
                                    {section.items.map((item) => (
                                        <button
                                            key={item.id}
                                            onClick={() => setActiveItemId(item.id)}
                                            className={cn(
                                                "w-full text-left px-3 py-2.5 rounded-xl text-xs font-medium transition-all duration-200",
                                                activeItemId === item.id 
                                                    ? "bg-[hsl(var(--teal-vivid))]/10 text-[hsl(var(--teal-vivid))] border border-[hsl(var(--teal-vivid))]/20 shadow-sm"
                                                    : "text-muted-foreground hover:bg-[hsl(var(--card-border))]/50 hover:text-foreground"
                                            )}
                                        >
                                            {item.title}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Mobile Header/Nav */}
                <div className="md:hidden flex flex-col pt-4 px-4 bg-[hsl(var(--sidebar-bg))] border-b border-[hsl(var(--card-border))]">
                    <div className="flex items-center justify-between my-4">
                        <h2 className="text-xs font-bold text-foreground uppercase tracking-widest">User Guide</h2>
                    </div>
                    
                    <button 
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-xl mb-4 text-sm font-medium text-foreground"
                    >
                        <span className="truncate">{activeItem.section}: {activeItem.title}</span>
                        <ChevronDown className={cn("w-4 h-4 transition-transform", isMobileMenuOpen && "rotate-180")} />
                    </button>

                    {isMobileMenuOpen && (
                        <div className="absolute top-[120px] left-4 right-4 max-h-[60vh] overflow-y-auto bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-xl shadow-2xl z-50 p-2 animate-in slide-in-from-top-2 duration-200">
                            {GUIDE_DATA.map((section) => (
                                <div key={section.section} className="mb-4 last:mb-0">
                                    <div className="px-3 py-2 text-[11.5px] font-black text-muted-foreground uppercase tracking-[0.1em] border-b border-[hsl(var(--card-border))]/50 mb-1">
                                        {section.section}
                                    </div>
                                    {section.items.map((item) => (
                                        <button
                                            key={item.id}
                                            onClick={() => {
                                                setActiveItemId(item.id);
                                                setIsMobileMenuOpen(false);
                                            }}
                                            className={cn(
                                                "w-full text-left px-3 py-3 rounded-lg text-sm transition-colors",
                                                activeItemId === item.id ? "bg-[hsl(var(--teal-vivid))]/10 text-[hsl(var(--teal-vivid))]" : "text-foreground"
                                            )}
                                        >
                                            {item.title}
                                        </button>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Main Content Area */}
                <div className="flex-1 flex flex-col bg-[hsl(var(--main-bg))] min-h-0">
                    <div className="flex-1 overflow-y-auto p-4 md:px-8 md:py-6 flex flex-col scrollbar-none">
                        <div className="max-w-4xl w-full mx-auto space-y-4 h-full">
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <span className="px-2 py-0.5 rounded-full bg-[hsl(var(--card-border))] text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                                        {activeItem.section}
                                    </span>
                                </div>
                                <h1 className="text-base md:text-lg font-bold text-foreground leading-tight">
                                    {activeItem.title}
                                </h1>
                                <p className="text-xs md:text-sm text-muted-foreground">
                                    {activeItem.description}
                                </p>
                            </div>

                            {/* Video Player */}
                            <div 
                                className="relative aspect-video w-full bg-black rounded-2xl overflow-hidden border border-[hsl(var(--card-border))] shadow-xl group"
                                onContextMenu={(e) => e.preventDefault()}
                            >
                                {activeItem.videoUrl ? (
                                    <video 
                                        src={videoSrc}
                                        className={cn(
                                            "w-full h-full object-contain transition-opacity duration-300",
                                            isVideoReady ? "opacity-100" : "opacity-0"
                                        )}
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        disablePictureInPicture
                                        controlsList="nodownload nofullscreen noremoteplayback"
                                        onContextMenu={(e) => e.preventDefault()}
                                        onCanPlay={() => setIsVideoReady(true)}
                                        style={{ pointerEvents: 'none' }}
                                    />
                                ) : (
                                    <div className="w-full h-full flex flex-col items-center justify-center bg-[hsl(var(--card-bg))] text-muted-foreground gap-4">
                                        <div className="absolute inset-0 flex items-center justify-center opacity-[0.03]">
                                            <PlayCircle className="w-64 h-64" />
                                        </div>
                                        <PlayCircle className="w-12 h-12 text-[hsl(var(--teal-vivid))] opacity-20" />
                                        <div className="text-center z-10 px-6">
                                            <h3 className="text-base font-bold text-foreground/80 mb-1">Coming Soon</h3>
                                            <p className="text-xs text-muted-foreground/60 max-w-[240px] mx-auto">
                                                We're currently preparing the visual demo for this feature. Check back soon!
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Loading Overlay (shown while buffering on mobile) */}
                                {activeItem.videoUrl && !isVideoReady && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 animate-pulse">
                                        <div className="w-8 h-8 border-2 border-[hsl(var(--teal-vivid))]/30 border-t-[hsl(var(--teal-vivid))] rounded-full animate-spin" />
                                    </div>
                                )}

                                {/* Hidden Preloader: This forces the browser to actually download the next video's bytes */}
                                {(() => {
                                    const nextItem = allItems[activeIndex + 1];
                                    const nextUrl = nextItem ? prefetchCache[nextItem.id] : null;
                                    return nextUrl ? (
                                        <video 
                                            key={`preload-${nextItem.id}`}
                                            src={nextUrl} 
                                            preload="auto" 
                                            muted 
                                            style={{ display: 'none' }} 
                                        />
                                    ) : null;
                                })()}
                            </div>
                        </div>
                    </div>

                    {/* Footer Navigation */}
                    <div className="py-2 px-4 md:py-2.5 md:px-6 border-t border-[hsl(var(--card-border))] bg-[hsl(var(--card-bg))] flex items-center justify-between pb-2">
                        <div className="text-[10px] md:text-xs text-muted-foreground font-medium">
                            {activeIndex + 1} of {allItems.length} features
                        </div>
                        
                        <div className="flex items-center gap-2 md:gap-3">
                            <button 
                                onClick={handleBack}
                                disabled={activeIndex === 0}
                                className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl border border-[hsl(var(--card-border))] text-[13px] font-semibold text-foreground hover:bg-[hsl(var(--card-border))]/50 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft className="w-3.5 h-3.5" />
                                Back
                            </button>
                            {activeIndex === allItems.length - 1 ? (
                                <button
                                    onClick={onClose}
                                    className="flex items-center justify-center gap-1.5 px-5 py-2 rounded-xl bg-foreground text-background font-bold text-[13px] hover:opacity-90 transition-all"
                                >
                                    Close
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            ) : (
                                <button
                                    onClick={handleNext}
                                    className="flex items-center justify-center gap-1.5 px-5 py-2 rounded-xl bg-foreground text-background font-bold text-[13px] hover:opacity-90 transition-all"
                                >
                                    Next
                                    <ChevronRight className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

